import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ComponentFactoryResolver,
  ApplicationRef,
  Injector,
  AfterViewInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EsriExpand, EsriLayerList, EsriReactiveUtils } from '../../../config/esri.module';
import { MainMapService } from '../../../shared/service/main-map.service';
import { WidgetNotifyService } from '../../../shared/service/widget-notify.service';
import { LayerOpacityComponent } from './layer-opacity/layer-opacity.component';
import { LayerInfoComponent } from './layer-info/layer-info.component';
import { LayerDefinitionExpressionComponent } from './layer-definition-expression/layer-definition-expression.component';
import { BaseWidget } from '../base-widget/base-widget';

@Component({
    selector: 'layer-control',
    templateUrl: './layer-control.component.html',
    standalone: false
})
export class LayerControlComponent extends BaseWidget implements OnInit, AfterViewInit {
  @ViewChild('layerControlComponentRef', { static: true })
  layerControlComponentRef: ElementRef;
  @ViewChild('layerControlRef', { static: true }) layerControlRef: ElementRef;

  // esri widget instance
  private _layerList: __esri.LayerList;
  // opacity control tooltip
  private opacityControlToolTip: string;

  public widgetToolTip: string;

  constructor(
    public mainMapService: MainMapService,
    public widgetNotifyService: WidgetNotifyService,
    private _translate: TranslateService,
    private _factoryResolver: ComponentFactoryResolver,
    private applicationRef: ApplicationRef,
    private injector: Injector
  ) {
    super(mainMapService, widgetNotifyService);
  }

  ngOnInit() {
    const mapView: __esri.MapView = this.mainMapService.mapView;

    this._layerList = new EsriLayerList(this.createLayerListProperties(mapView));

    // translate tooltip
    this._translate.get('WIDGET.TOC.TOOLTIP').subscribe(msg => {
      this.widgetToolTip = msg;
    });

    // Create an expand instance and set the content
    const expandProperties: __esri.ExpandProperties = {
      view: mapView,
      id: 'LayerControlComponent',
      content: this.layerControlComponentRef.nativeElement,
      expandIcon: 'layers',
    };

    this.expandWidget = new EsriExpand(expandProperties);
    // translate tooltip icon
    this.translateToolTipWidgetIcon(this.expandWidget, this._translate, 'WIDGET.TOC.TOOLTIP');
    // listen on language change
    this._translate.onLangChange.subscribe(() => {
      this.translateToolTipWidgetIcon(this.expandWidget, this._translate, 'WIDGET.TOC.TOOLTIP');
      this.translateListItems(this._layerList.operationalItems);
    });

    // translate opacity-control tooltip
    this._translate.get('JSON_CONFIG.LAYERLIST.TOGGLE_OPACITY').subscribe(txt => {
      this.opacityControlToolTip = txt;
    });

    // add widget to the main map
    mapView.ui.add(this.expandWidget, 'top-right');
    this.initWidgetListeners();
  }

  ngAfterViewInit() {
    EsriReactiveUtils.when(
      () => this._layerList.viewModel.state === 'ready',
      () => {
        this.translateListItems(this._layerList.operationalItems);
      }
    );
  }

  private createLayerListProperties(mapView: __esri.MapView): __esri.LayerListProperties {
    // create esri widget instance
    return {
      view: mapView,
      container: this.layerControlRef.nativeElement,
      listItemCreatedFunction: e => this.listItemCreated(e),
      visibleElements: {
        statusIndicators: false,
      },
    };
  }

  /**
   * @name translateListItem
   * @param listItem
   * @description Translates a listItem with his children
   */
  private translateListItem(listItem: __esri.ListItem) {
    const key: string = listItem.layer.title;
    this._translate.get(key).subscribe((translate: string) => {
      listItem.title = key !== translate ? translate : listItem.layer.title;
    });

    // translate children
    this.translateListItems(listItem.children);
  }

  /**
   * @name translateListItems
   * @param listItems Collection of listItems
   * @description Translates all the listItems
   */
  private translateListItems(listItems: __esri.Collection) {
    listItems.forEach(listItem => {
      this.translateListItem(listItem);
    });
  }

  private listItemCreated = event => {
    const item = event.item;

    if (item.layer) {
      const fLay = item.layer;
      item.visible = item.layer.visible;

      // also, do not allow to change opacity on sublayers... (sublayers are identified by opacity === undefined)
      if (fLay.disableOpacityControl !== true && fLay.opacity !== undefined) {
        const panelContent = document.createElement('div');
        this.appendOpacitySliderCtrl(panelContent, fLay);

        // define action for opactiy controller
        // (also see https://developers.arcgis.com/javascript/latest/api-reference/esri-support-actions-ActionButton.html)
        item.panel = {
          content: panelContent,
          open: false,
          title: this.opacityControlToolTip,
          visible: fLay.visible,
          image: 'assets/images/icon-opacity.png',
        };

        // add layer watcher for visibility changes (to hide the opacity action in case of invisible layers)
        EsriReactiveUtils.watch(
          () => fLay?.visible,
          (visibilityValue: boolean) => {
            item.panel.visible = visibilityValue;
            item.panel.open = false;
          }
        );
      }

      this.addItemFunctionsToLayerList(item);
    }
  };

  private addItemFunctionsToLayerList(item: any) {
    if (item.layer.itemFunctions) {
      item.layer.itemFunctions.forEach(itemFunc => {
        const panelContent = document.createElement('div');

        if (itemFunc.id === 'definitionExpression') {
          this.appendDefinitionExpressionCtrl(panelContent, itemFunc, item);
        } else {
          this.appendInfoCtrl(panelContent, itemFunc);
        }

        // translate control icon tooltip
        let tooltip = '';
        this._translate.get(itemFunc.iconTooltip).subscribe(txt => {
          tooltip = txt;
        });

        item.panel = {
          content: panelContent,
          open: false,
          title: tooltip,
          className: itemFunc.icon,
        };
      });
    } else {
      return;
    }
  }

  private appendOpacitySliderCtrl(panelContent: HTMLDivElement, featureLayer: any) {
    const factory = this._factoryResolver.resolveComponentFactory(LayerOpacityComponent);
    const opacityCmp = factory.create(this.injector, [], panelContent);
    opacityCmp.instance.featureLayer = featureLayer;
    // enable change-detection in component:
    this.applicationRef.attachView(opacityCmp.hostView);
  }

  private appendInfoCtrl(panelContent: HTMLDivElement, itemFunction: any) {
    const factory = this._factoryResolver.resolveComponentFactory(LayerInfoComponent);
    const layerInfoComp = factory.create(this.injector, [], panelContent);
    layerInfoComp.instance.itemFunction = itemFunction;
    // enable change-detection in component:
    this.applicationRef.attachView(layerInfoComp.hostView);
  }

  private appendDefinitionExpressionCtrl(
    panelContent: HTMLDivElement,
    itemFunction: any,
    item: any
  ) {
    const factory = this._factoryResolver.resolveComponentFactory(
      LayerDefinitionExpressionComponent
    );
    const layerInfoComp = factory.create(this.injector, [], panelContent);
    layerInfoComp.instance.itemFunction = itemFunction;
    layerInfoComp.instance.esriLayer = item;
    // enable change-detection in component:
    this.applicationRef.attachView(layerInfoComp.hostView);
  }
}
